import React, { Component } from "react";
import moment from "moment";

import "./Translation.css";

const fullName = (last, first) => [last, first].filter(Boolean).join(", ");

const formatMoney = (value) =>
  value == null
    ? ""
    : Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Status flags as text, never color alone. */
const cardFlags = (job, isLastColumn) => {
  const flags = [];
  if (job.isDeletedFlg) flags.push({ key: "deleted", label: "Deleted" });
  if (job.invoicedFlg) flags.push({ key: "invoiced", label: "Invoiced" });
  if (
    !job.isDeletedFlg &&
    !isLastColumn &&
    job.jobDateTime &&
    moment(job.jobDateTime).isBefore(moment(), "day")
  ) {
    flags.push({ key: "overdue", label: "Overdue" });
  }
  return flags;
};

/**
 * Kanban view of translations, one column per Translation workflow item. Cards move by
 * drag and drop or, without a mouse, with the arrow buttons on each card. Moves are
 * requested through onMove; the parent owns the data and reverts when the server rejects.
 */
export class TranslationBoard extends Component {
  static displayName = TranslationBoard.name;

  constructor(props) {
    super(props);
    this.state = { draggingJobId: null, dragOverColumn: null };
  }

  onDragStart = (e, job) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", job.jobId);
    this.setState({ draggingJobId: job.jobId });
  };

  onDragEnd = () => this.setState({ draggingJobId: null, dragOverColumn: null });

  onDragOver = (e, column) => {
    if (!this.state.draggingJobId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dragOverColumn !== column) this.setState({ dragOverColumn: column });
  };

  onDrop = (e, column) => {
    e.preventDefault();
    const jobId = e.dataTransfer.getData("text/plain");
    const job = this.props.translations.find((t) => t.jobId === jobId);
    this.onDragEnd();
    if (job && job.workFlowId !== column) this.props.onMove(job, column);
  };

  renderCard(job, columnIndex) {
    const { columns, onOpen, onMove } = this.props;
    const isLast = columnIndex === columns.length - 1;
    const canMove = !job.isDeletedFlg;
    const prev = columnIndex > 0 ? columns[columnIndex - 1] : null;
    const next = !isLast ? columns[columnIndex + 1] : null;
    const label = `Translation #${job.jobNum}`;
    const translator = fullName(job.intLastName, job.intFirstName);
    const rep = fullName(job.repLastName, job.repFirstName);

    return (
      <li
        key={job.jobId}
        className={[
          "tr-kcard",
          job.isDeletedFlg ? "tr-kcard-deleted" : "",
          this.state.draggingJobId === job.jobId ? "tr-kcard-dragging" : "",
        ].join(" ")}
        draggable={canMove}
        onDragStart={(e) => this.onDragStart(e, job)}
        onDragEnd={this.onDragEnd}
      >
        <button
          type="button"
          className="tr-kcard-open"
          onClick={() => onOpen(job)}
          aria-label={`Open ${label}`}
        >
          <span className="tr-kcard-top">
            <span className="tr-kcard-num">#{job.jobNum}</span>
            <span className="tr-kcard-flags">
              {cardFlags(job, isLast).map((f) => (
                <span key={f.key} className={`tr-pill tr-pill-${f.key}`}>
                  {f.label}
                </span>
              ))}
            </span>
          </span>
          <span className="tr-kcard-client">
            {job.clientAbbrv && <span className="tr-chip-abbrv">{job.clientAbbrv}</span>}
            {job.clientName}
          </span>
          {job.jobDateTime && (
            <span className="tr-kcard-line">
              {moment(job.jobDateTime).format("MMM D, YYYY · h:mm A")}
            </span>
          )}
          {translator && (
            <span className="tr-kcard-line">
              <span className="tr-kcard-label">Translator</span> {translator}
            </span>
          )}
          {rep && (
            <span className="tr-kcard-line">
              <span className="tr-kcard-label">Rep</span> {rep}
            </span>
          )}
          {job.callerNote && <span className="tr-kcard-note">{job.callerNote}</span>}
        </button>

        <div className="tr-kcard-footer">
          <span className="tr-kcard-total">{formatMoney(job.jobTotal)}</span>
          {canMove && (
            <span className="tr-kcard-moves">
              <button
                type="button"
                className="tr-icon-btn"
                disabled={!prev}
                onClick={() => onMove(job, prev)}
                aria-label={prev ? `Move ${label} back to ${prev}` : `${label} is in the first stage`}
                title={prev ? `Move to ${prev}` : undefined}
              >
                ‹
              </button>
              <button
                type="button"
                className="tr-icon-btn"
                disabled={!next}
                onClick={() => onMove(job, next)}
                aria-label={next ? `Move ${label} forward to ${next}` : `${label} is in the last stage`}
                title={next ? `Move to ${next}` : undefined}
              >
                ›
              </button>
            </span>
          )}
        </div>
      </li>
    );
  }

  render() {
    const { columns, translations } = this.props;

    return (
      <div className="tr-board" role="region" aria-label="Translation board">
        {columns.map((column, index) => {
          const jobs = translations.filter((t) => t.workFlowId === column);
          const headingId = `tr-col-${index}`;
          return (
            <section
              key={column}
              className={`tr-column${this.state.dragOverColumn === column ? " tr-column-over" : ""}`}
              aria-labelledby={headingId}
              onDragOver={(e) => this.onDragOver(e, column)}
              onDragLeave={() => this.setState({ dragOverColumn: null })}
              onDrop={(e) => this.onDrop(e, column)}
            >
              <header className="tr-column-header">
                <h2 id={headingId} className="tr-column-title">
                  {column}
                </h2>
                <span className="tr-column-count" aria-label={`${jobs.length} translations`}>
                  {jobs.length}
                </span>
              </header>
              {jobs.length === 0 ? (
                <p className="tr-column-empty">No translations</p>
              ) : (
                <ul className="tr-column-cards">{jobs.map((job) => this.renderCard(job, index))}</ul>
              )}
            </section>
          );
        })}
      </div>
    );
  }
}
